import { memo, useState, useEffect } from 'react';
import Image, { ImageProps } from 'next/image';
import { useMemo } from 'react';
import cx from 'classnames';
import { supportWebpDomain } from '@/constants/url'; // 支持webp的图片域名
import { convertToWebP } from '@/utils/image';
import Styles from './index.module.scss';

const defaultDevice = require('@/assets/icon/default-device.png');
const defaultAvatar = `${process.env.RESOURCE_URL}uc_server/images/noavatar_middle.gif`;
export const defaultImage = require('@/assets/icon/defaultCover.png');

export enum CutModeEnum {
  cover = 'cover',
  contain = 'contain',
  fill = 'fill',
  none = 'none',
}

export enum ImageType {
  avatar = 'avatar',
  image = 'image',
  icon = 'icon',
  device = 'device',
}

export interface ICommonImage {
  draggable?: ImageProps['draggable'];
  className?: string;
  id?: string;
  src: ImageProps['src'];
  alt?: ImageProps['alt'];
  layout?:
    | 'fill'
    | [Required<ImageProps['width']>, Required<ImageProps['height']>];
  cutMode?: keyof typeof CutModeEnum;
  priority?: ImageProps['priority'];
  placeholderImage?: ImageProps['blurDataURL'];
  isHeightAuto?: boolean;
  width?: number;
  height?: number;
  type?: ImageType;
  loading?: ImageProps['loading'];
  ratio?: string;
  onError?: ()=>void;
  onClick?: ()=>void;
}

const CommonImage = ({
  draggable,
  src,
  className = '',
  id,
  layout = 'fill',
  cutMode,
  alt = '大疆社区',
  priority = false,
  placeholderImage,
  width = 10,
  height = 10,
  isHeightAuto = false,
  type,
  loading,
  ratio,
  onError,
  onClick,
}: ICommonImage) => {
  // 拼接webp后缀
  const webpSrc = typeof src !== 'string' ? src : convertToWebP(src as string, ratio);
  // webp模式实际显示的图片资源
  const [currentSrc, setCurrentSrc] = useState(webpSrc as string);

  useEffect(() => {
    setCurrentSrc(webpSrc as string);
  }, [webpSrc])


  const isWebpSupported = useMemo(() => {
    // 判断src的类型是否是string,使用"src={require('@/assets/icon/V.png')}"方式引入的图片资源src是一个对象
    if (!src || typeof src !== 'string') return false;
    // 判断当前src的域名是否在支持webp的域名列表中
    const domain = new URL(src).hostname;
    return supportWebpDomain.includes(domain);
  }, [src])

  const imageStyle = useMemo(() => {
    if (cutMode) {
      return {
        objectFit: cutMode,
      };
    }
  }, [cutMode]);

  const placeholderProps: Pick<ImageProps, 'placeholder' | 'blurDataURL'> =
    placeholderImage
      ? {
          placeholder: 'blur',
          blurDataURL: placeholderImage,
        }
      : { placeholder: 'empty' };

  const handleError = (e: any) => {
    if (type === ImageType.avatar) {
      e.target.src = defaultAvatar;
      e.target.srcset = '';
    } else if (type === ImageType.device) {
      e.target.src = defaultDevice?.default?.src;
      e.target.srcset = '';
    } else {
      e.target.src = defaultImage?.default?.src;
      e.target.srcset = '';
      e.target.style.objectFit = 'cover';
      e.target.style.backgroundColor = '#e6e6e6';
    }
    onError?.();
  };

  const handleSourceError = () => {
    if (currentSrc === webpSrc) {
      // 第一轮: webp图片加载失败,替换为普通图片
      setCurrentSrc(src as string);
    } else if (currentSrc === src) {
      // 第二轮: 普通图片加载失败,替换为默认图片
      if (type === ImageType.avatar) {
        setCurrentSrc(defaultAvatar);
      } else if (type === ImageType.device) {
        setCurrentSrc(defaultDevice?.default?.src);
      } else {
        setCurrentSrc(defaultImage?.default?.src);
      }
      onError?.();
    }
  };

  const customLoader = ({ src }: { src: string }) => {
    return src;
  }

  const renderImage = (useSourceError?: boolean) => {
    if (isHeightAuto) {
      return <Image
              className={cx(Styles.CommomImage, className)}
              id={id}
              src={src}
              draggable={draggable}
              alt="pic"
              width={width}
              height={10}
              loader={customLoader}
              style={{
                width: '100%',
                height: 'auto',
                objectFit: cutMode
              }}
              loading={loading}
              onClick={onClick}
              onError={useSourceError ? handleSourceError : handleError}
            />
    }
    // 填充模式
    else if (layout === 'fill') {
      return (
        <Image
          className={cx(Styles.CommomImage, className)}
          id={id}
          src={src}
          draggable={draggable}
          alt={alt}
          style={imageStyle}
          width={width}
          height={height}
          priority={priority}
          loader={customLoader}
          {...placeholderProps}
          onError={useSourceError ? handleSourceError : handleError}
          loading={loading}
          onClick={onClick}
        />
      );
    }
    // 固定比例模式
    return (
      <Image
        className={className}
        id={id}
        src={src}
        alt={alt}
        draggable={draggable}
        style={imageStyle}
        width={layout[0]}
        height={layout[1]}
        priority={priority}
        loader={customLoader}
        {...placeholderProps}
        loading={loading}
        onClick={onClick}
        onError={useSourceError ? handleSourceError : handleError}
      />
    );
  }

  // webp模式
  if (!!isWebpSupported) {
    return (
      <picture style={{ display: 'contents' }}>
        <source srcSet={currentSrc} type="image/webp" />
        {renderImage(true)}
      </picture>
    )
  }
  // 普通模式
  return renderImage();
};

export default memo(CommonImage);
